import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, MoreVertical, X, ChevronDown, Eye, Copy, Play, RefreshCw, CheckCircle2, AlertTriangle, Clock, BarChart3, Star, FileText, Users, TrendingUp, TrendingDown, ThumbsDown, Ban, Cpu, Timer, Shield, ShieldAlert, MessageSquare, Send, Trash2, ArrowDown, ArrowUp, Layers, Target, Brain, Sparkles, FileCode2, AlertCircle, Zap, PenLine, RotateCcw, XCircle, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
interface AIQualityControlCenterProps {
  success: (msg: string) => void;
  setDetailPanel: (panel: any) => void;
}
// --- TYPES ---
interface QualityReport {
  id: string;
  userId: string;
  userName: string;
  reportType: string;
  templateId: string;
  templateName: string;
  model: string;
  rating: number;
  complaint: string | null;
  aiTags: string[];
  responseLength: number;
  genTime: number;
  tokens: number;
  cost: number;
  status: 'open' | 'resolved' | 'ignored' | 'retry';
  astrologer: string | null;
  retryCount: number;
  createdAt: string;
  resolvedAt: string | null;
  pdfUrl: string;
}
// --- HELPERS ---
function rand<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randNum(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function randFloat(min: number, max: number, dec = 1) {
  return parseFloat((Math.random() * (max - min) + min).toFixed(dec));
}
// --- CONSTANTS ---
const REPORT_TYPES = ['Натальная карта', 'Соляр', 'Совместимость', 'Прогноз', 'Комбо'];
const TEMPLATES = [{
  id: 'TPL-01',
  name: 'Натальная карта'
}, {
  id: 'TPL-02',
  name: 'Соляр'
}, {
  id: 'TPL-03',
  name: 'Совместимость'
}, {
  id: 'TPL-04',
  name: 'Прогноз'
}, {
  id: 'TPL-05',
  name: 'Комбо'
}, {
  id: 'TPL-06',
  name: 'Натальная карта (Premium)'
}, {
  id: 'TPL-07',
  name: 'Прогноз (Extended)'
}];
const MODELS = ['GPT-5', 'Claude 3.5', 'Llama 3'];
const USERS = ['Анна К.', 'Дмитрий П.', 'Елена С.', 'Михаил В.', 'Ольга Н.', 'Сергей Т.', 'Наталья Б.', 'Алексей Г.', 'Ирина Д.', 'Павел М.', 'Юлия Р.', 'Андрей Л.', 'Мария Ф.', 'Виктор З.', 'Татьяна Х.'];
const ASTROLOGERS = ['Астролог Мария', 'Астролог Алексей', 'Астролог Елена', null, null, null];
const AI_TAGS = ['повтор текста', 'short answer', 'hallucination', 'incoherent', 'wrong data', 'formatting issues', 'missing sections', 'low personalization'];
const TAG_COLORS: Record<string, string> = {
  'повтор текста': 'bg-amber-50 text-amber-700 border-amber-200',
  'short answer': 'bg-orange-50 text-orange-700 border-orange-200',
  hallucination: 'bg-rose-50 text-rose-700 border-rose-200',
  incoherent: 'bg-red-50 text-red-700 border-red-200',
  'wrong data': 'bg-rose-50 text-rose-700 border-rose-200',
  'formatting issues': 'bg-blue-50 text-blue-700 border-blue-200',
  'missing sections': 'bg-violet-50 text-violet-700 border-violet-200',
  'low personalization': 'bg-slate-100 text-slate-700 border-slate-200'
};
const COMPLAINTS = ['Отчёт слишком короткий и неинформативный', 'Повторяющийся текст в нескольких секциях', 'Неправильные данные о планетах', 'Отсутствует раздел про карьеру', 'Текст не соответствует моим данным', 'Слишком общие рекомендации', 'Ошибки в форматировании PDF', 'Нет персонализации, шаблонный текст', 'Галлюцинации — упомянуты несуществующие аспекты', 'Противоречивые утверждения в разных секциях', null, null, null];
const STATUSES: QualityReport['status'][] = ['open', 'resolved', 'ignored', 'retry'];
const STATUS_CFG: Record<string, {
  label: string;
  variant: any;
  icon: any;
}> = {
  open: {
    label: 'Открыт',
    variant: 'warning',
    icon: AlertCircle
  },
  resolved: {
    label: 'Решён',
    variant: 'success',
    icon: CheckCircle2
  },
  ignored: {
    label: 'Игнорирован',
    variant: 'neutral',
    icon: Ban
  },
  retry: {
    label: 'Retry',
    variant: 'info',
    icon: RefreshCw
  }
};
// --- MOCK DATA ---
function generateReports(count: number): QualityReport[] {
  return Array.from({
    length: count
  }, (_, i) => {
    const tpl = rand(TEMPLATES);
    const rating = i < 20 ? randFloat(1.0, 2.9) : i < 50 ? randFloat(3.0, 3.9) : randFloat(1.0, 4.9);
    const hasComplaint = rating < 3.5 || Math.random() > 0.7;
    const tagCount = rating < 2.5 ? randNum(2, 4) : rating < 3.5 ? randNum(1, 2) : Math.random() > 0.6 ? 1 : 0;
    const tags: string[] = [];
    const available = [...AI_TAGS];
    for (let j = 0; j < tagCount; j++) {
      if (available.length === 0) break;
      const idx = randNum(0, available.length - 1);
      tags.push(available.splice(idx, 1)[0]);
    }
    const status = rating < 3.0 ? Math.random() > 0.3 ? 'open' : rand(['resolved', 'retry']) : rand(STATUSES);
    return {
      id: `QR-${String(i + 1).padStart(4, '0')}`,
      userId: `USR-${String(randNum(1, 200)).padStart(3, '0')}`,
      userName: rand(USERS),
      reportType: tpl.name.split(' (')[0],
      templateId: tpl.id,
      templateName: tpl.name,
      model: rand(MODELS),
      rating,
      complaint: hasComplaint ? rand(COMPLAINTS.filter(Boolean) as string[]) : null,
      aiTags: tags,
      responseLength: randNum(800, 8000),
      genTime: randNum(8, 90),
      tokens: randNum(1500, 16000),
      cost: randFloat(0.02, 0.85, 2),
      status: status as QualityReport['status'],
      astrologer: rand(ASTROLOGERS),
      retryCount: status === 'retry' ? randNum(1, 3) : 0,
      createdAt: new Date(Date.now() - randNum(0, 30) * 24 * 60 * 60 * 1000).toISOString(),
      resolvedAt: status === 'resolved' ? new Date(Date.now() - randNum(0, 5) * 24 * 60 * 60 * 1000).toISOString() : null,
      pdfUrl: `#pdf-${i + 1}`
    };
  });
}
const INITIAL_DATA = generateReports(120);
// Chart data
function genChartDays(days: number) {
  return Array.from({
    length: days
  }, (_, i) => {
    const d = new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000);
    return {
      date: d.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit'
      }),
      rating: randFloat(3.8, 4.8),
      complaints: randNum(0, 8),
      good: randNum(60, 95),
      bad: randNum(2, 15)
    };
  });
}
const CHART_DATA = genChartDays(14);
const MODEL_STATS = [{
  model: 'GPT-5',
  rating: 4.6,
  usage: 4200,
  errors: 2.1,
  time: 22
}, {
  model: 'Claude 3.5',
  rating: 4.2,
  usage: 2800,
  errors: 3.8,
  time: 28
}, {
  model: 'Llama 3',
  rating: 3.9,
  usage: 1100,
  errors: 5.2,
  time: 35
}];
const TEMPLATE_QUALITY = TEMPLATES.map(t => ({
  ...t,
  rating: randFloat(3.5, 4.9),
  complaints: randNum(0, 25),
  reports: randNum(100, 2000),
  problematic: Math.random() > 0.7
}));
export function AIQualityControlCenter({
  success,
  setDetailPanel
}: AIQualityControlCenterProps) {
  const [data, setData] = useState(INITIAL_DATA);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    rating: 'all',
    model: 'all',
    template: 'all',
    tag: 'all',
    hasComplaint: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [sortField, setSortField] = useState('rating');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showDetail, setShowDetail] = useState(false);
  const [selectedReport, setSelectedReport] = useState<QualityReport | null>(null);
  const [detailTab, setDetailTab] = useState('pdf');
  const [activeView, setActiveView] = useState<'table' | 'analytics'>('table');
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);
  useEffect(() => {
    const h = () => setOpenMenuId(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);
  // Live update simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setData(prev => {
        if (Math.random() > 0.7) {
          const newReport = generateReports(1)[0];
          newReport.id = `QR-${String(prev.length + 1).padStart(4, '0')}`;
          newReport.rating = randFloat(1.0, 2.5);
          newReport.status = 'open';
          newReport.createdAt = new Date().toISOString();
          return [newReport, ...prev];
        }
        return prev;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  // Stats
  const stats = useMemo(() => {
    const total = data.length;
    const good = data.filter(d => d.rating >= 4.0).length;
    const bad = data.filter(d => d.rating < 3.0).length;
    const complaints = data.filter(d => d.complaint).length;
    const open = data.filter(d => d.status === 'open').length;
    const avgRating = data.reduce((s, d) => s + d.rating, 0) / total;
    const avgLength = Math.round(data.reduce((s, d) => s + d.responseLength, 0) / total);
    const avgTime = Math.round(data.reduce((s, d) => s + d.genTime, 0) / total);
    const retried = data.filter(d => d.retryCount > 0).length;
    return {
      total,
      good,
      bad,
      complaints,
      open,
      avgRating: avgRating.toFixed(1),
      goodPct: (good / total * 100).toFixed(1),
      badPct: (bad / total * 100).toFixed(1),
      avgLength,
      avgTime,
      retryPct: (retried / total * 100).toFixed(1)
    };
  }, [data]);
  // Filter & Sort
  const filteredData = useMemo(() => {
    let result = data.filter(item => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || item.id.toLowerCase().includes(q) || item.userName.toLowerCase().includes(q) || item.templateName.toLowerCase().includes(q);
      const matchStatus = filters.status === 'all' || item.status === filters.status;
      const matchRating = filters.rating === 'all' || filters.rating === 'low' && item.rating < 3.0 || filters.rating === 'mid' && item.rating >= 3.0 && item.rating < 4.0 || filters.rating === 'high' && item.rating >= 4.0;
      const matchModel = filters.model === 'all' || item.model === filters.model;
      const matchTemplate = filters.template === 'all' || item.templateId === filters.template;
      const matchTag = filters.tag === 'all' || item.aiTags.includes(filters.tag);
      const matchComplaint = filters.hasComplaint === 'all' || filters.hasComplaint === 'yes' && item.complaint || filters.hasComplaint === 'no' && !item.complaint;
      // KPI filter
      const matchKpi = !kpiFilter || kpiFilter === 'good' && item.rating >= 4.0 || kpiFilter === 'bad' && item.rating < 3.0 || kpiFilter === 'complaints' && item.complaint || kpiFilter === 'open' && item.status === 'open' || kpiFilter === 'retry' && item.retryCount > 0;
      return matchSearch && matchStatus && matchRating && matchModel && matchTemplate && matchTag && matchComplaint && matchKpi;
    });
    result.sort((a, b) => {
      const key = sortField as keyof QualityReport;
      const av = a[key],
        bv = b[key];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;else if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return result;
  }, [data, searchQuery, filters, sortField, sortDir, kpiFilter]);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const openDetail = (r: QualityReport) => {
    setSelectedReport(r);
    setDetailTab('pdf');
    setShowDetail(true);
  };
  const updateStatus = (id: string, status: QualityReport['status']) => {
    setData(prev => prev.map(d => d.id === id ? {
      ...d,
      status,
      resolvedAt: status === 'resolved' ? new Date().toISOString() : d.resolvedAt
    } : d));
    success(`${id}: ${STATUS_CFG[status]?.label}`);
  };
  const SortHeader = ({
    field,
    children
  }: {
    field: string;
    children: React.ReactNode;
  }) => <th className="px-3 py-3 font-medium cursor-pointer hover:text-indigo-600 select-none" onClick={() => handleSort(field)} data-id="element-8149">
      <span className="flex items-center gap-1" data-id="element-8150">
        {children}
        {sortField === field && <ChevronDown className={`w-3 h-3 transition-transform ${sortDir === 'desc' ? 'rotate-180' : ''}`} data-id="element-8151" />}
      </span>
    </th>;
  const KpiCard = ({
    label,
    value,
    icon: Icon,
    color,
    filterKey,
    active
  }: any) => <button onClick={() => {
    setKpiFilter(kpiFilter === filterKey ? null : filterKey);
    setCurrentPage(1);
    setActiveView('table');
  }} className={`text-left p-4 rounded-xl border transition-all ${active ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-8152">
      <div className="flex items-center gap-3" data-id="element-8153">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`} data-id="element-8154">
          <Icon className="w-4 h-4" data-id="element-8155" />
        </div>
        <div data-id="element-8156">
          <div className="text-xl font-bold text-slate-900" data-id="element-8157">{value}</div>
          <div className="text-[10px] text-slate-500" data-id="element-8158">{label}</div>
        </div>
      </div>
    </button>;
  // --- DETAIL MODAL ---
  const renderDetail = () => {
    if (!selectedReport) return null;
    const r = selectedReport;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDetail(false)} data-id="element-8159">
        <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-8160">
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8161">
            <div data-id="element-8162">
              <div className="flex items-center gap-3 mb-1" data-id="element-8163">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-8164">
                  Отчёт {r.id}
                </h2>
                <Badge variant={STATUS_CFG[r.status]?.variant} data-id="element-8165">
                  {STATUS_CFG[r.status]?.label}
                </Badge>
                <div className="flex items-center gap-1" data-id="element-8166">
                  <Star className={`w-4 h-4 ${r.rating >= 4 ? 'text-amber-500 fill-amber-500' : r.rating >= 3 ? 'text-amber-400 fill-amber-400' : 'text-rose-500 fill-rose-500'}`} data-id="element-8167" />
                  <span className={`text-sm font-bold ${r.rating >= 4 ? 'text-amber-600' : r.rating >= 3 ? 'text-amber-500' : 'text-rose-600'}`} data-id="element-8168">
                    {r.rating}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-500" data-id="element-8169">
                {r.reportType} • {r.model} • {r.templateName} • {r.userName}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-8170">
              <Button variant="outline" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-8172" />} onClick={() => {
              updateStatus(r.id, 'retry');
              success('Retry запущен');
            }} data-id="element-8171">
                Retry
              </Button>
              <Button variant="outline" size="sm" icon={<CheckCircle2 className="w-4 h-4" data-id="element-8174" />} onClick={() => updateStatus(r.id, 'resolved')} data-id="element-8173">
                Решён
              </Button>
              <button onClick={() => setShowDetail(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 ml-2" data-id="element-8175">
                <X className="w-5 h-5" data-id="element-8176" />
              </button>
            </div>
          </div>

          <div className="px-6 border-b border-slate-100 bg-white shrink-0" data-id="element-8177">
            <Tabs activeTab={detailTab} onChange={setDetailTab} tabs={[{
            id: 'pdf',
            label: 'PDF отчёт'
          }, {
            id: 'rating',
            label: 'Оценка'
          }, {
            id: 'complaint',
            label: 'Жалоба'
          }, {
            id: 'ai',
            label: 'AI анализ'
          }, {
            id: 'prompt',
            label: 'Prompt'
          }, {
            id: 'retry',
            label: 'Retry история'
          }, {
            id: 'template',
            label: 'Шаблон'
          }]} data-id="element-8178" />
          </div>

          <div className="p-6 overflow-y-auto bg-slate-50 flex-1" data-id="element-8179">
            {detailTab === 'pdf' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8180">
                <div className="flex items-center justify-between mb-4" data-id="element-8181">
                  <h3 className="text-sm font-bold text-slate-900" data-id="element-8182">
                    PDF отчёт
                  </h3>
                  <div className="flex gap-2" data-id="element-8183">
                    <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" data-id="element-8185" />} data-id="element-8184">
                      Открыть PDF
                    </Button>
                    <Button variant="ghost" size="sm" icon={<Copy className="w-4 h-4" data-id="element-8187" />} onClick={() => success('Ссылка скопирована')} data-id="element-8186">
                      Копировать ссылку
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-8188">
                  {[['Тип', r.reportType], ['Длина', `${r.responseLength} символов`], ['Токены', r.tokens.toLocaleString()], ['Стоимость', `$${r.cost}`]].map(([l, v]) => <div key={l as string} className="p-3 bg-slate-50 rounded-lg" data-id="element-8189">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-8190">
                        {l}
                      </div>
                      <div className="text-sm font-bold text-slate-900 mt-1" data-id="element-8191">
                        {v}
                      </div>
                    </div>)}
                </div>
                <div className="border border-slate-200 rounded-lg p-8 bg-white text-center" data-id="element-8192">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-8193" />
                  <p className="text-sm text-slate-500" data-id="element-8194">
                    Предпросмотр PDF отчёта
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-8195">
                    Нажмите "Открыть PDF" для полного просмотра
                  </p>
                </div>
              </Card>}

            {detailTab === 'rating' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8196">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8197">
                  Оценка пользователя
                </h3>
                <div className="flex items-center gap-4 mb-6" data-id="element-8198">
                  <div className="flex items-center gap-1" data-id="element-8199">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-6 h-6 ${s <= Math.round(r.rating) ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} data-id="element-8200" />)}
                  </div>
                  <span className="text-2xl font-bold text-slate-900" data-id="element-8201">
                    {r.rating}
                  </span>
                  <span className="text-sm text-slate-500" data-id="element-8202">/ 5.0</span>
                </div>
                <div className="space-y-3" data-id="element-8203">
                  {[['Пользователь', r.userName], ['ID пользователя', r.userId], ['Дата оценки', new Date(r.createdAt).toLocaleDateString('ru-RU')], ['Тип отчёта', r.reportType]].map(([l, v]) => <div key={l as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8204">
                      <span className="text-sm text-slate-500" data-id="element-8205">{l}</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8206">
                        {v}
                      </span>
                    </div>)}
                </div>
              </Card>}

            {detailTab === 'complaint' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8207">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8208">
                  Жалоба
                </h3>
                {r.complaint ? <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg mb-4" data-id="element-8209">
                    <div className="flex items-start gap-3" data-id="element-8210">
                      <ThumbsDown className="w-5 h-5 text-rose-500 mt-0.5 shrink-0" data-id="element-8211" />
                      <div data-id="element-8212">
                        <p className="text-sm text-rose-800 font-medium mb-1" data-id="element-8213">
                          Текст жалобы:
                        </p>
                        <p className="text-sm text-rose-700" data-id="element-8214">{r.complaint}</p>
                      </div>
                    </div>
                  </div> : <div className="p-8 text-center text-slate-400" data-id="element-8215">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" data-id="element-8216" />
                    <p className="text-sm" data-id="element-8217">Жалоб нет</p>
                  </div>}
                {r.complaint && <div className="flex gap-2 mt-4" data-id="element-8218">
                    <Button variant="outline" size="sm" icon={<Send className="w-4 h-4" data-id="element-8220" />} onClick={() => success('Отправлено астрологу')} data-id="element-8219">
                      На доработку
                    </Button>
                    <Button variant="outline" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-8222" />} onClick={() => success('Retry запущен')} data-id="element-8221">
                      Retry
                    </Button>
                    <Button variant="ghost" size="sm" icon={<Ban className="w-4 h-4" data-id="element-8224" />} onClick={() => updateStatus(r.id, 'ignored')} data-id="element-8223">
                      Игнорировать
                    </Button>
                  </div>}
              </Card>}

            {detailTab === 'ai' && <div className="space-y-6" data-id="element-8225">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8226">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8227">
                    AI Quality Tags
                  </h3>
                  {r.aiTags.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-8228">
                      {r.aiTags.map((tag, i) => <span key={i} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${TAG_COLORS[tag] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-id="element-8229">
                          {tag}
                        </span>)}
                    </div> : <p className="text-sm text-slate-400" data-id="element-8230">Нет тегов качества</p>}
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8231">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8232">
                    Метрики генерации
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-8233">
                    {[{
                  label: 'Время',
                  value: `${r.genTime} сек`,
                  icon: Timer,
                  color: 'bg-amber-100 text-amber-600'
                }, {
                  label: 'Токены',
                  value: r.tokens.toLocaleString(),
                  icon: Zap,
                  color: 'bg-indigo-100 text-indigo-600'
                }, {
                  label: 'Длина',
                  value: `${r.responseLength}`,
                  icon: FileText,
                  color: 'bg-blue-100 text-blue-600'
                }, {
                  label: 'Стоимость',
                  value: `$${r.cost}`,
                  icon: Target,
                  color: 'bg-emerald-100 text-emerald-600'
                }].map(m => <div key={m.label} className="p-3 bg-white border border-slate-200 rounded-lg" data-id="element-8234">
                        <div className="flex items-center gap-2 mb-1" data-id="element-8235">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${m.color}`} data-id="element-8236">
                            <m.icon className="w-3 h-3" data-id="element-8237" />
                          </div>
                          <span className="text-[10px] text-slate-500" data-id="element-8238">
                            {m.label}
                          </span>
                        </div>
                        <div className="text-lg font-bold text-slate-900" data-id="element-8239">
                          {m.value}
                        </div>
                      </div>)}
                  </div>
                </Card>
              </div>}

            {detailTab === 'prompt' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8240">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8241">
                  Prompt использованный для генерации
                </h3>
                <div className="mb-4" data-id="element-8242">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8243">
                    System Prompt
                  </h4>
                  <div className="p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto leading-relaxed" data-id="element-8244">
                    You are an expert astrologer specializing in{' '}
                    {r.reportType.toLowerCase()} analysis. Based on the provided
                    planetary positions, generate a comprehensive
                    interpretation...
                  </div>
                </div>
                <div data-id="element-8245">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8246">
                    User Prompt
                  </h4>
                  <div className="p-4 bg-slate-900 text-blue-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto leading-relaxed" data-id="element-8247">
                    {`Сгенерируй отчёт "${r.reportType}" для клиента.
Модель: ${r.model}
Шаблон: ${r.templateName}
Данные: {{birth_date}}, {{birth_time}}, {{birth_place}}`}
                  </div>
                </div>
              </Card>}

            {detailTab === 'retry' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8248">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8249">
                  История Retry ({r.retryCount})
                </h3>
                {r.retryCount > 0 ? <div className="space-y-3" data-id="element-8250">
                    {Array.from({
                length: r.retryCount
              }, (_, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 bg-white" data-id="element-8251">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-8252">
                            <RefreshCw className="w-4 h-4 text-indigo-600" data-id="element-8253" />
                          </div>
                          <div className="flex-1" data-id="element-8254">
                            <div className="flex items-center gap-2 mb-1" data-id="element-8255">
                              <span className="text-sm font-bold text-slate-900" data-id="element-8256">
                                Retry #{i + 1}
                              </span>
                              <Badge variant={i === r.retryCount - 1 ? 'warning' : 'success'} size="sm" data-id="element-8257">
                                {i === r.retryCount - 1 ? 'Последний' : 'Завершён'}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-600" data-id="element-8258">
                              Модель: {rand(MODELS)} • Время: {randNum(10, 50)}{' '}
                              сек • Рейтинг: {randFloat(2.5, 4.5)}
                            </p>
                          </div>
                        </div>)}
                  </div> : <div className="text-center py-8 text-slate-400" data-id="element-8259">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 text-slate-300" data-id="element-8260" />
                    <p className="text-sm" data-id="element-8261">Retry не выполнялся</p>
                  </div>}
              </Card>}

            {detailTab === 'template' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8262">
                <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8263">
                  Связанный шаблон
                </h3>
                <div className="space-y-3" data-id="element-8264">
                  {[['ID шаблона', r.templateId], ['Название', r.templateName], ['Тип отчёта', r.reportType], ['Модель', r.model]].map(([l, v]) => <div key={l as string} className="flex justify-between py-2 border-b border-slate-100" data-id="element-8265">
                      <span className="text-sm text-slate-500" data-id="element-8266">{l}</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-8267">
                        {v}
                      </span>
                    </div>)}
                </div>
                <Button variant="outline" size="sm" className="mt-4" icon={<FileCode2 className="w-4 h-4" data-id="element-8269" />} onClick={() => success(`Открыт шаблон ${r.templateId}`)} data-id="element-8268">
                  Открыть шаблон
                </Button>
              </Card>}
          </div>
        </Card>
      </div>;
  };
  // --- ANALYTICS VIEW ---
  const renderAnalytics = () => <div className="space-y-6" data-id="element-8270">
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-8271">
        <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8272">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8273">
            Качество по дням
          </h3>
          <ResponsiveContainer width="100%" height={220} data-id="element-8274">
            <AreaChart data={CHART_DATA} data-id="element-8275">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-8276" />
              <XAxis dataKey="date" tick={{
              fontSize: 10
            }} stroke="#94a3b8" data-id="element-8277" />
              <YAxis domain={[3, 5]} tick={{
              fontSize: 10
            }} stroke="#94a3b8" data-id="element-8278" />
              <RTooltip contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }} data-id="element-8279" />
              <Area type="monotone" dataKey="rating" stroke="#6366f1" fill="#eef2ff" strokeWidth={2} name="Ср. рейтинг" data-id="element-8280" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8281">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8282">
            Жалобы по дням
          </h3>
          <ResponsiveContainer width="100%" height={220} data-id="element-8283">
            <BarChart data={CHART_DATA} data-id="element-8284">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-8285" />
              <XAxis dataKey="date" tick={{
              fontSize: 10
            }} stroke="#94a3b8" data-id="element-8286" />
              <YAxis tick={{
              fontSize: 10
            }} stroke="#94a3b8" data-id="element-8287" />
              <RTooltip contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0'
            }} data-id="element-8288" />
              <Bar dataKey="complaints" fill="#f43f5e" radius={[4, 4, 0, 0]} name="Жалобы" data-id="element-8289" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Model comparison */}
      <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8290">
        <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8291">
          Качество по моделям
        </h3>
        <div className="overflow-x-auto" data-id="element-8292">
          <table className="w-full text-left" data-id="element-8293">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200" data-id="element-8294">
              <tr data-id="element-8295">
                <th className="px-4 py-3" data-id="element-8296">Модель</th>
                <th className="px-4 py-3" data-id="element-8297">Ср. рейтинг</th>
                <th className="px-4 py-3" data-id="element-8298">Использований</th>
                <th className="px-4 py-3" data-id="element-8299">Ошибки %</th>
                <th className="px-4 py-3" data-id="element-8300">Ср. время</th>
                <th className="px-4 py-3" data-id="element-8301">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-8302">
              {MODEL_STATS.map(m => <tr key={m.model} className="hover:bg-slate-50" data-id="element-8303">
                  <td className="px-4 py-3" data-id="element-8304">
                    <div className="flex items-center gap-2" data-id="element-8305">
                      <Cpu className="w-4 h-4 text-indigo-500" data-id="element-8306" />
                      <span className="text-sm font-medium text-slate-900" data-id="element-8307">
                        {m.model}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-8308">
                    <div className="flex items-center gap-1" data-id="element-8309">
                      <Star className={`w-3.5 h-3.5 ${m.rating >= 4.5 ? 'text-amber-500 fill-amber-500' : 'text-amber-400 fill-amber-400'}`} data-id="element-8310" />
                      <span className="text-sm font-bold" data-id="element-8311">{m.rating}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-id="element-8312">
                    {m.usage.toLocaleString()}
                  </td>
                  <td className="px-4 py-3" data-id="element-8313">
                    <span className={`text-sm font-medium ${m.errors > 4 ? 'text-rose-600' : 'text-slate-600'}`} data-id="element-8314">
                      {m.errors}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600" data-id="element-8315">
                    {m.time} сек
                  </td>
                  <td className="px-4 py-3" data-id="element-8316">
                    <Badge variant={m.rating >= 4.5 ? 'success' : m.rating >= 4.0 ? 'warning' : 'error'} size="sm" data-id="element-8317">
                      {m.rating >= 4.5 ? 'Отлично' : m.rating >= 4.0 ? 'Хорошо' : 'Проблемно'}
                    </Badge>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Template quality + Top complaints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-8318">
        <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8319">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8320">
            Качество по шаблонам
          </h3>
          <div className="space-y-2" data-id="element-8321">
            {TEMPLATE_QUALITY.sort((a, b) => a.rating - b.rating).map(t => <div key={t.id} className={`flex items-center justify-between p-3 rounded-lg border ${t.problematic ? 'border-rose-200 bg-rose-50/30' : 'border-slate-100 bg-white'}`} data-id="element-8322">
                <div className="flex items-center gap-3 min-w-0" data-id="element-8323">
                  {t.problematic && <ShieldAlert className="w-4 h-4 text-rose-500 shrink-0" data-id="element-8324" />}
                  <div className="min-w-0" data-id="element-8325">
                    <div className="text-sm font-medium text-slate-900 truncate" data-id="element-8326">
                      {t.name}
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-8327">
                      {t.id} • {t.reports} отчётов • {t.complaints} жалоб
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0" data-id="element-8328">
                  <Star className={`w-3.5 h-3.5 ${t.rating >= 4.5 ? 'text-amber-500 fill-amber-500' : t.rating >= 4.0 ? 'text-amber-400 fill-amber-400' : 'text-rose-500 fill-rose-500'}`} data-id="element-8329" />
                  <span className={`text-sm font-bold ${t.rating >= 4.0 ? 'text-slate-900' : 'text-rose-600'}`} data-id="element-8330">
                    {t.rating}
                  </span>
                </div>
              </div>)}
          </div>
        </Card>
        <Card padding="md" className="shadow-sm border-slate-200" data-id="element-8331">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-8332">
            Топ причин жалоб
          </h3>
          <div className="space-y-3" data-id="element-8333">
            {AI_TAGS.map((tag, i) => {
            const count = data.filter(d => d.aiTags.includes(tag)).length;
            const pct = data.length > 0 ? (count / data.length * 100).toFixed(1) : '0';
            return <div key={tag} className="flex items-center gap-3" data-id="element-8334">
                  <span className="text-xs text-slate-400 w-5 text-right" data-id="element-8335">
                    {i + 1}
                  </span>
                  <div className="flex-1" data-id="element-8336">
                    <div className="flex items-center justify-between mb-1" data-id="element-8337">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${TAG_COLORS[tag] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-id="element-8338">
                        {tag}
                      </span>
                      <span className="text-xs text-slate-500" data-id="element-8339">
                        {count} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8340">
                      <div className="h-full bg-rose-400 rounded-full" style={{
                    width: `${Math.min(parseFloat(pct) * 3, 100)}%`
                  }} data-id="element-8341" />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </Card>
      </div>
    </div>;
  // --- MAIN RENDER ---
  return <div className="space-y-6" data-id="element-8342">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-id="element-8343">
        <KpiCard label="Ср. рейтинг" value={stats.avgRating} icon={Star} color="bg-amber-100 text-amber-600" filterKey={null} active={false} data-id="element-8344" />
        <KpiCard label="Хороших (≥4.0)" value={`${stats.goodPct}%`} icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" filterKey="good" active={kpiFilter === 'good'} data-id="element-8345" />
        <KpiCard label="Плохих (<3.0)" value={`${stats.badPct}%`} icon={TrendingDown} color="bg-rose-100 text-rose-600" filterKey="bad" active={kpiFilter === 'bad'} data-id="element-8346" />
        <KpiCard label="Жалобы" value={stats.complaints} icon={ThumbsDown} color="bg-orange-100 text-orange-600" filterKey="complaints" active={kpiFilter === 'complaints'} data-id="element-8347" />
        <KpiCard label="Ср. длина" value={`${stats.avgLength}`} icon={FileText} color="bg-blue-100 text-blue-600" filterKey={null} active={false} data-id="element-8348" />
        <KpiCard label="Ср. время" value={`${stats.avgTime}с`} icon={Timer} color="bg-violet-100 text-violet-600" filterKey={null} active={false} data-id="element-8349" />
        <KpiCard label="% Retry" value={`${stats.retryPct}%`} icon={RefreshCw} color="bg-indigo-100 text-indigo-600" filterKey="retry" active={kpiFilter === 'retry'} data-id="element-8350" />
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2" data-id="element-8351">
        <button onClick={() => setActiveView('table')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeView === 'table' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-8352">
          Таблица отчётов
        </button>
        <button onClick={() => setActiveView('analytics')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeView === 'analytics' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-8353">
          Аналитика качества
        </button>
        {kpiFilter && <button onClick={() => {
        setKpiFilter(null);
        setCurrentPage(1);
      }} className="ml-2 px-3 py-1.5 text-xs text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 flex items-center gap-1" data-id="element-8354">
            <X className="w-3 h-3" data-id="element-8355" /> Сбросить фильтр KPI
          </button>}
      </div>

      {activeView === 'analytics' ? renderAnalytics() : <Card padding="none" className="shadow-sm border-slate-200" data-id="element-8356">
          {/* Toolbar */}
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100" data-id="element-8357">
            <div className="flex-1 min-w-[200px] max-w-sm relative" data-id="element-8358">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-8359" />
              <input type="text" placeholder="Поиск по ID, пользователю, шаблону..." value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-8360" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-8362" />} onClick={() => setShowFilters(!showFilters)} data-id="element-8361">
              Фильтры
            </Button>
            <span className="text-xs text-slate-500 ml-auto" data-id="element-8363">
              {filteredData.length} отчётов
            </span>
          </div>

          {showFilters && <div className="p-4 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-3 items-end" data-id="element-8364">
              {[{
          key: 'status',
          label: 'Статус',
          options: [{
            value: 'all',
            label: 'Все'
          }, {
            value: 'open',
            label: 'Открыт'
          }, {
            value: 'resolved',
            label: 'Решён'
          }, {
            value: 'ignored',
            label: 'Игнорирован'
          }, {
            value: 'retry',
            label: 'Retry'
          }]
        }, {
          key: 'rating',
          label: 'Рейтинг',
          options: [{
            value: 'all',
            label: 'Все'
          }, {
            value: 'low',
            label: '< 3.0'
          }, {
            value: 'mid',
            label: '3.0–3.9'
          }, {
            value: 'high',
            label: '≥ 4.0'
          }]
        }, {
          key: 'model',
          label: 'Модель',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...MODELS.map(m => ({
            value: m,
            label: m
          }))]
        }, {
          key: 'template',
          label: 'Шаблон',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...TEMPLATES.map(t => ({
            value: t.id,
            label: t.name
          }))]
        }, {
          key: 'tag',
          label: 'AI Tag',
          options: [{
            value: 'all',
            label: 'Все'
          }, ...AI_TAGS.map(t => ({
            value: t,
            label: t
          }))]
        }, {
          key: 'hasComplaint',
          label: 'Жалоба',
          options: [{
            value: 'all',
            label: 'Все'
          }, {
            value: 'yes',
            label: 'Есть'
          }, {
            value: 'no',
            label: 'Нет'
          }]
        }].map(f => <div key={f.key} data-id="element-8365">
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block" data-id="element-8366">
                    {f.label}
                  </label>
                  <select value={(filters as any)[f.key]} onChange={e => {
            setFilters(prev => ({
              ...prev,
              [f.key]: e.target.value
            }));
            setCurrentPage(1);
          }} className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-8367">
                    {f.options.map(o => <option key={o.value} value={o.value} data-id="element-8368">
                        {o.label}
                      </option>)}
                  </select>
                </div>)}
              <Button variant="ghost" size="sm" onClick={() => {
          setFilters({
            status: 'all',
            rating: 'all',
            model: 'all',
            template: 'all',
            tag: 'all',
            hasComplaint: 'all'
          });
          setCurrentPage(1);
        }} data-id="element-8369">
                Сбросить
              </Button>
            </div>}

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-8370">
            <table className="w-full text-left" style={{
          minWidth: '1400px'
        }} data-id="element-8371">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-8372">
                <tr data-id="element-8373">
                  <th className="px-3 py-3 font-medium" data-id="element-8374">ID</th>
                  <th className="px-3 py-3 font-medium" data-id="element-8375">Пользователь</th>
                  <th className="px-3 py-3 font-medium" data-id="element-8376">Тип</th>
                  <th className="px-3 py-3 font-medium" data-id="element-8377">Шаблон</th>
                  <th className="px-3 py-3 font-medium" data-id="element-8378">Модель</th>
                  <SortHeader field="rating" data-id="element-8379">Оценка</SortHeader>
                  <th className="px-3 py-3 font-medium" data-id="element-8380">Жалоба</th>
                  <th className="px-3 py-3 font-medium" data-id="element-8381">AI Tags</th>
                  <SortHeader field="responseLength" data-id="element-8382">Длина</SortHeader>
                  <SortHeader field="genTime" data-id="element-8383">Время</SortHeader>
                  <th className="px-3 py-3 font-medium" data-id="element-8384">Статус</th>
                  <th className="px-3 py-3 w-10" data-id="element-8385"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-8386">
                {paginatedData.map(r => <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${r.rating < 3.0 ? 'bg-rose-50/30' : ''}`} data-id="element-8387">
                    <td className="px-3 py-3" data-id="element-8388">
                      <button className="text-xs font-bold text-indigo-600 hover:underline" onClick={() => openDetail(r)} data-id="element-8389">
                        {r.id}
                      </button>
                    </td>
                    <td className="px-3 py-3" data-id="element-8390">
                      <button className="text-xs text-slate-700 hover:text-indigo-600 hover:underline" onClick={() => setDetailPanel({
                  type: 'user',
                  title: r.userName,
                  data: {
                    id: r.userId
                  }
                })} data-id="element-8391">
                        {r.userName}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600" data-id="element-8392">
                      {r.reportType}
                    </td>
                    <td className="px-3 py-3" data-id="element-8393">
                      <button className="text-xs text-indigo-600 hover:underline" onClick={() => success(`Открыт шаблон ${r.templateId}`)} data-id="element-8394">
                        {r.templateId}
                      </button>
                    </td>
                    <td className="px-3 py-3" data-id="element-8395">
                      <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-8396">
                        {r.model}
                      </Badge>
                    </td>
                    <td className="px-3 py-3" data-id="element-8397">
                      <button className="flex items-center gap-1" onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    rating: r.rating < 3 ? 'low' : r.rating < 4 ? 'mid' : 'high'
                  }));
                  setCurrentPage(1);
                }} data-id="element-8398">
                        <Star className={`w-3 h-3 ${r.rating >= 4 ? 'text-amber-500 fill-amber-500' : r.rating >= 3 ? 'text-amber-400 fill-amber-400' : 'text-rose-500 fill-rose-500'}`} data-id="element-8399" />
                        <span className={`text-xs font-bold ${r.rating >= 4 ? 'text-amber-600' : r.rating >= 3 ? 'text-amber-500' : 'text-rose-600'}`} data-id="element-8400">
                          {r.rating}
                        </span>
                      </button>
                    </td>
                    <td className="px-3 py-3" data-id="element-8401">
                      {r.complaint ? <span className="text-xs text-slate-600 max-w-[150px] block truncate" title={r.complaint} data-id="element-8402">
                          {r.complaint}
                        </span> : <span className="text-xs text-slate-300" data-id="element-8403">—</span>}
                    </td>
                    <td className="px-3 py-3" data-id="element-8404">
                      <div className="flex flex-wrap gap-1 max-w-[180px]" data-id="element-8405">
                        {r.aiTags.slice(0, 2).map((tag, i) => <span key={i} className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${TAG_COLORS[tag] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-id="element-8406">
                            {tag}
                          </span>)}
                        {r.aiTags.length > 2 && <span className="text-[9px] text-slate-400" data-id="element-8407">
                            +{r.aiTags.length - 2}
                          </span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600" data-id="element-8408">
                      {r.responseLength.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600" data-id="element-8409">
                      {r.genTime}с
                    </td>
                    <td className="px-3 py-3" data-id="element-8410">
                      <button onClick={() => updateStatus(r.id, r.status === 'open' ? 'resolved' : 'open')} data-id="element-8411">
                        <Badge variant={STATUS_CFG[r.status]?.variant} size="sm" className="cursor-pointer hover:opacity-80 text-[10px]" data-id="element-8412">
                          {STATUS_CFG[r.status]?.label}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-3 py-3 relative" data-id="element-8413">
                      <button className="p-1 text-slate-400 hover:text-slate-600 rounded" onClick={e => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === r.id ? null : r.id);
                }} data-id="element-8414">
                        <MoreVertical className="w-4 h-4" data-id="element-8415" />
                      </button>
                      {openMenuId === r.id && <div className="absolute right-0 top-9 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[9999]" data-id="element-8416">
                          <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    openDetail(r);
                    setOpenMenuId(null);
                  }} data-id="element-8417">
                            <Eye className="w-3.5 h-3.5" data-id="element-8418" /> Открыть отчёт (PDF)
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    updateStatus(r.id, 'retry');
                    setOpenMenuId(null);
                  }} data-id="element-8419">
                            <RefreshCw className="w-3.5 h-3.5" data-id="element-8420" /> Retry
                            генерации
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    success('Модель изменена');
                    setOpenMenuId(null);
                  }} data-id="element-8421">
                            <Cpu className="w-3.5 h-3.5" data-id="element-8422" /> Сменить модель
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    success('Отправлено астрологу');
                    setOpenMenuId(null);
                  }} data-id="element-8423">
                            <Send className="w-3.5 h-3.5" data-id="element-8424" /> На доработку
                            астрологу
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-8425" />
                          <button className="w-full text-left px-4 py-2 text-xs text-emerald-600 hover:bg-emerald-50 flex items-center gap-2" onClick={() => {
                    updateStatus(r.id, 'resolved');
                    setOpenMenuId(null);
                  }} data-id="element-8426">
                            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-8427" /> Отметить
                            решённым
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-500 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    updateStatus(r.id, 'ignored');
                    setOpenMenuId(null);
                  }} data-id="element-8428">
                            <Ban className="w-3.5 h-3.5" data-id="element-8429" /> Игнорировать
                          </button>
                        </div>}
                    </td>
                  </tr>)}
                {paginatedData.length === 0 && <tr data-id="element-8430">
                    <td colSpan={12} className="text-center py-12 text-slate-500" data-id="element-8431">
                      <Shield className="w-10 h-10 mx-auto mb-3 text-slate-300" data-id="element-8432" />
                      <p className="text-sm font-medium" data-id="element-8433">Отчётов не найдено</p>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex items-center justify-between" data-id="element-8434">
              <span className="text-xs text-slate-500" data-id="element-8435">
                Показано {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, filteredData.length)} из{' '}
                {filteredData.length}
              </span>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-8436" />
            </div>}
        </Card>}

      {showDetail && renderDetail()}
    </div>;
}